/**
 * Build campaign font CSS from the campaign's stored googleFont + fontClass.
 *
 * Campaign Creator can store Google fonts as bare family names. Legacy
 * campaigns can store googleFont as a full @import rule, and asset-pool custom
 * fonts can store it as a full @font-face block. This helper accepts each
 * shape and returns CSS declarations App.vue can inject directly.
 */

export interface CampaignFontCss {
	importRule: string;
	fontFamilyRule: string;
}

const FALLBACK_FONT_FAMILY_RULE = "font-family: Arial, Helvetica, sans-serif;";

function startsWithCi(value: string, prefix: string): boolean {
	return value.toLowerCase().startsWith(prefix.toLowerCase());
}

function extractFamilyFromImport(importRule: string): string {
	const match = importRule.match(/family=([^:&)]+)/i);
	if (!match) return "";
	try {
		return decodeURIComponent(match[1]).replace(/\+/g, " ").trim();
	} catch {
		return match[1].replace(/\+/g, " ").trim();
	}
}

function extractFamilyFromFontFace(fontFaceRule: string): string {
	const match = fontFaceRule.match(
		/font-family\s*:\s*(?:"([^"]+)"|'([^']+)'|([^;,}]+))/i,
	);
	if (!match) return "";
	return (match[1] ?? match[2] ?? match[3] ?? "").trim();
}

function buildImportRuleFromBareName(fontName: string): string {
	return `@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;700&display=swap');`;
}

function buildFontFamilyRuleFromName(fontName: string): string {
	return `font-family: "${fontName}", sans-serif;`;
}

export function buildCampaignFontCss(
	rawGoogleFont: string | null | undefined,
	rawFontClass: string | null | undefined,
): CampaignFontCss {
	const googleFont = (rawGoogleFont ?? "").trim();
	const fontClass = (rawFontClass ?? "").trim();

	let importRule = "";
	let derivedFamilyName = "";

	if (googleFont) {
		if (startsWithCi(googleFont, "@import")) {
			importRule = googleFont;
			derivedFamilyName = extractFamilyFromImport(googleFont);
		} else if (startsWithCi(googleFont, "@font-face")) {
			importRule = googleFont;
			derivedFamilyName = extractFamilyFromFontFace(googleFont);
		} else {
			importRule = buildImportRuleFromBareName(googleFont);
			derivedFamilyName = googleFont;
		}
	}

	let fontFamilyRule: string;
	if (startsWithCi(fontClass, "font-family")) {
		fontFamilyRule = fontClass.endsWith(";") ? fontClass : `${fontClass};`;
	} else if (derivedFamilyName) {
		fontFamilyRule = buildFontFamilyRuleFromName(derivedFamilyName);
	} else {
		fontFamilyRule = FALLBACK_FONT_FAMILY_RULE;
	}

	return { importRule, fontFamilyRule };
}
